import { useCallback, useEffect, useRef, useState } from 'react'
import { CASES } from '../mock/cases'
import { CaseCompare } from './CaseCompare'
import { DropZone } from './DropZone'
import { IconChevronRight } from '../icons'
import { BLOG_HREF } from '../links'
import { asset } from '../lib/asset'

/*
 * 首屏与案例区。
 *
 * 背景：2026-09-10 用户的三次裁决，一次比一次砍得多。
 *   「不是 AI 图上看不见的纹理」——标题要点名 Image 2 与数字涟漪，这个现象有名字、
 *     有出处、可复现，不能说成玄学。
 *   「最好是第二屏，给明显一些的」——对比器从右半栏挪到第二屏整幅宽。
 *   「界面太啰嗦了，重点讲用户价值就行了」——这一版删掉的都是**机制解释**：
 *     为什么不替你缩放、重采样会抹掉什么、镜片里外分别是哪一层……那些是我们的顾虑，
 *     不是用户的收益。用户要知道的只有三件：我的图有没有问题、修完什么样、要花多少。
 *     机制不是不能讲，而是留到它真正相关的那一屏（拒收卡讲上限、授权闸讲代价）。
 * 设计意图：第一屏一句话加一个拖入区，第二屏一张能自己说话的对比图。
 * 关键约束：
 *   1. 对比器整页只出现一次（2026-09-10：「右边预览就好了，不要右边也有下面也有」）。
 *   2. 一次只挂一条案例：一条两张原像素图约 1.7 MB，五条全挂等于背五倍的图。
 *      轮播只预取**下一条**，不预取全部。
 *   3. 切换案例时整个对比器重建（key）：淡入淡出要两条案例同时在场（见 case-stage），
 *      而放大镜那套 ref 里存着按当前图算的尺寸与位移，复用同一个实例会拿旧尺寸
 *      画错位的一帧。
 *   4. 指针不在案例区里时自动往下切（2026-09-10：「用鼠标如果没进来的时候，
 *      最好就变成自动轮播」）。进来——标签、图、底下那排按钮——一律停。
 *      `?case=` 钉住一条，给截图和走查用，轮播不跑。
 */

/**
 * 从 URL 读要显示哪条案例，例如 ?case=N。
 *
 * 与 ?state= 同一个用途：让每条案例都能直达，于是无头截图能逐条截，
 * 也能把某一条的链接发给别人看。取值不认就退回第一条，不抛错。
 */
function initialCase(): string {
  const q = new URLSearchParams(window.location.search).get('case')
  return CASES.some(c => c.id === q) ? (q as string) : CASES[0].id
}

/** 地址栏钉了某一条（?case=N）时轮播不许抢。截图和走查都靠这个。 */
function casePinned(): boolean {
  return new URLSearchParams(window.location.search).has('case')
}

/** 每条在画面上停多久。够下一条（约 1.7 MB 两张原像素图）预取完再切。 */
const DWELL_MS = 5500
const FADE_MS = 520

function nextCase(id: string): string {
  const i = CASES.findIndex(c => c.id === id)
  return CASES[(i + 1 + CASES.length) % CASES.length].id
}

function prefetchViews(id: string) {
  const c = CASES.find(x => x.id === id)
  if (!c) return
  for (const side of [c.before, c.after]) {
    const im = new Image()
    im.src = asset(side.src)
  }
}

/*
 * 图槽的形状：五条案例里**最高**的那一条。
 *
 * 四条链是 16:9，夜景长发是 19:12，同一显示宽度下高出 85 像素。按各自的高度渲染，
 * 点一下案例标签整页就上下跳一截（2026-09-10 用户裁决：这个按钮不应该跳页面的位置）。
 * 所以槽按最高的那条留，矮的那几条上下留黑边。
 *
 * 用 pixels（成品的原始宽高）算而不是写死 3/2：案例是脚本生成的，
 * 哪天换一条 4:5 的进来，这里跟着变，不必有人记得回来改 CSS。
 * slot-max 是那条案例按显示宽度铺开后的高度，防止窗口很高时槽比图还高、白留一片。
 */
const TALLEST = CASES.reduce((t, c) => {
  const [w, h] = c.before.pixels
  return h / w > t.r ? { r: h / w, w, h } : t
}, { r: 0, w: 1, h: 1 })
/** 整幅的显示宽度，等于内容线（app.css 的 .wrap）。
    与 make_case_assets.py 的 DISPLAY_LONG_EDGE 是同一个数，改 .wrap 时三处一起改。 */
const DISPLAY_LONG_EDGE = 1240
const slotStyle = {
  '--slot-ar': String(TALLEST.w / TALLEST.h),
  '--slot-max': `${Math.round(DISPLAY_LONG_EDGE * TALLEST.r)}px`,
} as React.CSSProperties

export function Landing({ onPick, guard }: {
  onPick: (f: File) => void
  /** 上传门禁，原样转给拖入区（返回 false 表示这次不许上传，见 DropZone）。 */
  guard?: () => boolean
}) {
  const [id, setId] = useState(initialCase)
  const [departingId, setDepartingId] = useState<string | null>(null)
  const [hold, setHold] = useState(false)
  const [hidden, setHidden] = useState(() => document.hidden)
  const play = useRef<HTMLDivElement>(null)
  const fadeTimer = useRef<number | null>(null)
  const active = CASES.find(c => c.id === id) ?? CASES[0]
  const departing = CASES.find(c => c.id === departingId)
  const pinned = casePinned()

  const showCase = useCallback((nextId: string) => {
    if (nextId === id) return
    if (fadeTimer.current !== null) window.clearTimeout(fadeTimer.current)
    setDepartingId(id)
    setId(nextId)
    fadeTimer.current = window.setTimeout(() => {
      setDepartingId(null)
      fadeTimer.current = null
    }, FADE_MS)
  }, [id])

  useEffect(() => () => {
    if (fadeTimer.current !== null) window.clearTimeout(fadeTimer.current)
  }, [])

  useEffect(() => {
    const onVis = () => setHidden(document.hidden)
    document.addEventListener('visibilitychange', onVis)
    return () => document.removeEventListener('visibilitychange', onVis)
  }, [])

  // 原生 pointerenter，不走 React 合成事件。放大镜那一套已经证实
  // Chrome 无头的鼠标移动能打到 addEventListener，打不到 onPointerEnter。
  useEffect(() => {
    const el = play.current
    if (!el) return
    const enter = () => setHold(true)
    const leave = () => setHold(false)
    el.addEventListener('pointerenter', enter)
    el.addEventListener('pointerleave', leave)
    return () => {
      el.removeEventListener('pointerenter', enter)
      el.removeEventListener('pointerleave', leave)
    }
  }, [])

  // 下一条的两张图先拉进缓存。切的时候对比器会整棵拆掉重建，
  // 缓存没命中就会先闪一帧空槽，再把图弹出来。
  useEffect(() => {
    prefetchViews(nextCase(id))
  }, [id])

  useEffect(() => {
    if (pinned || hold || hidden) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const t = window.setTimeout(() => showCase(nextCase(id)), DWELL_MS)
    return () => window.clearTimeout(t)
  }, [id, hold, hidden, pinned, showCase])

  return (
    <>
      <header className="lead">
        {/* 中文段落一律拆成 {'…'} 分块，避免 JSX 把换行折成一个可见空格。
            详见 scripts/check_jsx_cjk_wrap.py。 */}
        <h1>
          {'修复 '}<span className="grad">Image 2</span>{' 的数字涟漪'}
        </h1>
        {/* 两句话各占一行（2026-09-10 用户裁决：句号后面应该换行）。仍然是一个 <p>：
            它们是一件事的两半（先说处境，再给承诺），拆成两段会在段间距上多一层节奏。

            第一句说的是用户的处境，不是我们的功能。三版「我们不破坏你的图」的写法都被
            否掉了（2026-09-10：「不够吸引人，不够有价值感」）——那是自证清白，
            而看这一页的人此刻的处境是：一张构图、光、表情全对的图，放大发现细节碎了，
            而重新生成会换掉那张脸。**他真正怕的是那张图不可复得**，不是「工具会破坏它」。
            同类站里最有力的一句正是这个路子：Let's Enhance 的段头
            「Rescue the shots you almost lost」，先讲你差点失去的那张，再讲修。

            第二句才给承诺，而且承诺要落在他怕的那件事上，不落在管线词汇上。
            上一版写的是「能无损切除的绝不重画，画面和尺寸都不动」：「无损切除」「重画」
            是这个仓库内部的说法，「尺寸都不动」读起来像规格表。对照 Krea 的
            「Natural enhancement without artifacts」与 Photoroom 的「Edits that stay
            true to the product」——它们都只说结果，不说机制。

            ⚠️ 承诺的是「你不用回去重抽一张」，**不是**「我们不重新生成」。
            后者是假话：深度修复正是「参考级净化 → 以净化图为唯一垫图重生成 →
            无损格子切除」，整幅会重画一遍，链 H 的尺寸还从 1280×720 变成 1672×941，
            而结果页的 ComparePanel 就照实写着这件事。首屏若承诺不重生成，
            用户走到那一屏会发现被骗（2026-09-10 用户指出：「深度处理的话，是会重新生成的呀」）。
            成立的那件事是：重画只垫他这张图，所以构图、人物、氛围跟着原图走——
            他不必回自己的出图工具改提示词再抽一次，那才是他真正怕丢的东西。
            「重抽」说的是他在别处那一下，不是我们这一步；对我们这一步一律叫
            「深度修复」，不叫「重新生成」（2026-09-09 用户裁决，见 Gates.tsx）。 */}
        <p className="lead-lines">
          <span>{'好不容易抽到的那张图，放大一看细节全碎了。'}</span>
          <span>{'不用回去重抽一张，修完还是这张的构图、人物和氛围。'}</span>
        </p>
        <BlogLink />
        {/* compact：那条「按原始像素分析、不替你缩放」的长说明去掉了。
            它讲的是我们的实现顾虑，首屏用不上；真正需要它的时刻是
            图被拒收的那一屏，拒收卡会把上限、你这张多大、为什么不替你缩放一并说清。 */}
        <DropZone onPick={onPick} guard={guard} compact />
      </header>

      <section className="cases" style={slotStyle}>
        <div className="sec-head">
          <div className="kicker">Before / After</div>
          <h2>它修成什么样</h2>
          <p>提示词让画面塞满密集、重复的小细节，第一张就可能破碎；反复生成会越来越重。</p>
        </div>

        {/* 标签、图、底下那排按钮是同一块「进来」。指针在这块里就停，
            出去才接着切——人正在看放大镜或要点某一条时，不能从底下把图换掉。 */}
        <div className="cases-play" ref={play}>
          <div className="case-tabs">
            {CASES.map(c => (
              <button
                key={c.id}
                className={`case-tab${c.id === active.id ? ' on' : ''}`}
                onClick={() => showCase(c.id)}
              >
                {/* 标签上只有名字。原先挂过「叠瓦 23.2% → 0.5%」，去掉数字之后换成过一行
                    来源说明（复读脏图／用户原图），也去掉了（2026-09-09 用户裁决：
                    不需要给说明）。标签是用来点的。

                    右端那枚胶囊是唯一的例外：它说这张脏图是哪个模型出的
                    （2026-09-10 用户裁决）。同一个场景两代模型各有一条时，
                    光看标题分不出来；而「2.5 也照样涟漪」正是首屏那句副标题的证据。
                    无边框、灰底、小一号字——它是标注，不是第二个可点的东西。 */}
                <b>{c.title}</b>
                <i className="case-model">{c.model}</i>
              </button>
            ))}
          </div>

          {/* 标签底下原来还有一行灰字，写这条案例的画面成分（「地面碎冰与霜晶，
              矿物透光的密集小元素」）。2026-09-10 用户指着它说删掉：图就在下面，
              画面里有什么自己看得见，这行字只是把图说了一遍。
              substrate 这个字段也一并从数据和类型里去掉了，没人再读它。 */}
          <div className="case-stage">
            {departing && (
              <div className="case-layer is-leaving" key={departing.id}>
                <CaseCompare sample={departing} />
              </div>
            )}
            <div
              className={`case-layer${departing ? ' is-entering' : ''}`}
              key={active.id}
            >
              <CaseCompare sample={active} />
            </div>
          </div>
        </div>
      </section>

      {/* 这里曾有三张小卡：全幅原像素分析 / 先看见问题在哪几块 / 按米粒计费。
          2026-09-10 用户指着它们问「这几个玩意，有必要吗」——不必要，三条都是把别处
          已经说过的话在首屏再说一遍，而且是在人还没上传图、这三件事都还不相关的时候说：

            · 「不替你缩放」在拒收卡里（DropZone 非 compact 的那三句），
              那一刻他的图正因为超上限被挡下，这句话才有用；工作页顶上还有一枚同样的角标。
            · 「先看见问题在哪几块」由 ProblemMap 直接画在他自己那张图上，
              诊断一出来就自证，不需要预告。
            · 「按米粒计费、先报价、必须你授权」在授权闸上（Gates），
              那是他真正要掏米粒的那一步；页脚也常驻一行「处理按米粒计费」。

          首屏只留标题、一句处境、一句承诺、研究笔记和拖入区。 */}
    </>
  )
}

/**
 * 研究笔记的入口。
 *
 * 这一页只证明「修完什么样」，不解释「凭什么认定这是涟漪」。后者是一篇文章的量，
 * 讲在这里就又回到了用户嫌啰嗦的老路上；但它不能没有——这工具挂在米羊 Lab 底下，
 * 愿意往下追一层的人得有地方去。
 *
 * 它就是一个链接，没有第二种形态。文章还没发的时候链到 about:blank
 * （见 links.ts 的 BLOG_HREF），版面因此现在就是最终版面。
 */
function BlogLink() {
  return (
    <p className="lead-more">
      <a href={BLOG_HREF}>
        {'读研究笔记：涟漪是怎么被认出来的'}
        <IconChevronRight size={15} />
      </a>
    </p>
  )
}

